import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon, DropletsIcon, ZapIcon, ActivityIcon, LightbulbIcon,
  ToggleLeftIcon, GaugeIcon, WindIcon, CloudIcon, ChevronLeftIcon, ChevronRightIcon,
  PersonStandingIcon, RadarIcon, HeartPulseIcon, ShieldCheckIcon, LockIcon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { tbToChartData, tbToCombinedChartData } from '../utils/tbChartHelper';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';

import {
  TimeRange, Mode, SELMode, SEL_MODES, MODES, SensorDetailSidebarProps,
  TimeRangeTabs, ChartSection, PasswordToast, ToggleSwitch, SensorHeader
} from './SensorDetailShared';


export function SEODetail({ sensor, onBack, onClose, onUpdateSensor }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const telemetryKeys = useMemo(() => ['power_instant', 'temperature', 'humidity', 'plug_on','plug_mode'], []);
  const { data: realtimeData } = useTelemetry(deviceId, telemetryKeys);
  //const { data: realtimeData } = useHistoricalTelemetry(deviceId, 'DEVICE', telemetryKeys, 'realtime');

  const [envTimeRange, setEnvTimeRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [powerTimeRange, setPowerTimeRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');

  const powerKey = powerTimeRange === 'realtime' ? 'power_instant' :
                   powerTimeRange === 'daily' ? 'hourly_power' :
                   powerTimeRange === 'weekly' ? 'daily_power' : 'daily_power';

  const { data: histPower } = useTelemetryHistory(deviceId, [powerKey], powerTimeRange);
  const { data: histTempHumid } = useTelemetryHistory(deviceId, ['temperature', 'humidity'], envTimeRange);

  const finalPowerData = useMemo(() => {
    return powerTimeRange === 'realtime'
      ? tbToChartData(realtimeData, 'power_instant', 'realtime', [])
      : tbToChartData(histPower, powerKey, powerTimeRange, [], 0.001);
  }, [powerTimeRange, realtimeData, histPower, powerKey]);

  const finalTempHumidData = useMemo(() => {
    return envTimeRange === 'realtime'
      ? tbToCombinedChartData(realtimeData, 'temperature', 'temp', 'humidity', 'humidity', 'realtime', [])
      : tbToCombinedChartData(histTempHumid, 'temperature', 'temp', 'humidity', 'humidity', envTimeRange, []);
  }, [envTimeRange, realtimeData, histTempHumid]);

  const [mode, setMode] = useState<Mode>('normal');
  const [isOn, setIsOn] = useState(true);

  useEffect(() => {
    const switchState = realtimeData?.['plug_on'];
    const plugModeState = realtimeData?.['plug_mode'];
    if (switchState && switchState.length > 0) {
      const val = switchState[0].value;
      setIsOn(String(val) === 'true' || String(val) === 'on');
    }
    if (plugModeState && plugModeState.length > 0) {
      const val = Number(plugModeState[0].value);
      const matchedMode = MODES.find((m) => Math.floor(m.value) === Math.floor(val));
      if (matchedMode) {
        setMode(matchedMode.id);
      }
    }
  }, [realtimeData]);

  const handleToggleOutlet = async () => {
    const newValue = !isOn;
    setIsOn(newValue); 
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue?"on":"off"});
    } catch (error) {
      console.error('Failed to send RPC command for SEO plug switch:', error);
      setIsOn(!newValue); 
    }
    setTimeout(() => {
      try {
        thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue?"on":"off"});
      } catch (error) {
        console.error('Failed to send RPC command for SEO plug switch:', error);
        setIsOn(!newValue); 
      }
    }, 100);
  };

  const handleToggleMode = async (newMode: Mode) => {
    if (mode === 'always' && newMode !== 'always') {
      setPendingModeIndex(MODES.findIndex((m) => m.id === newMode));
      setShowPasswordVerify(true);
      return;
    }
    setMode(newMode);
    try {
      const targetMode = MODES.find((m) => m.id === newMode);
      if (targetMode) {
        await thingsboardService.sendOneWayRpc(deviceId, 'set_seo_mode', { value: targetMode.value });
      }
    } catch (error) {
      console.error('Failed to send RPC command for SEO plug mode:', error);
    }
  };  

  const [password, setPassword] = useState('0000');
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingModeIndex, setPendingModeIndex] = useState<number | null>(null);
  const currentModeIndex = MODES.findIndex((m) => m.id === mode);
  const navigateMode = (direction: -1 | 1) => {
    const nextIndex =
    (currentModeIndex + direction + MODES.length) % MODES.length;
    const nextMode = MODES[nextIndex];
    if (mode === 'always' && nextMode.id !== 'always') {
      setPendingModeIndex(nextIndex);
      setShowPasswordVerify(true);
      return;
    }
    handleToggleMode(nextMode.id);
  };
  const handlePasswordVerified = (success: boolean) => {
    setShowPasswordVerify(false);
    if (success && pendingModeIndex !== null) {
      const targetMode = MODES[pendingModeIndex];
      if (targetMode) {
        setMode(targetMode.id);
        thingsboardService.sendOneWayRpc(deviceId, 'set_seo_mode', { value: targetMode.value }).catch((error) => {
          console.error('Failed to send RPC command for SEO plug mode:', error);
        });
      }
    }
    setPendingModeIndex(null);
  };
  const handlePasswordChanged = (newPw: string) => {
    setPassword(newPw);
    setShowPasswordChange(false);
  };

  return (
    <>
      <div className="w-full flex flex-col gap-2 p-3 border-l border-white/20 bg-[#0a0f1a]/30 h-full overflow-hidden">
        <SensorHeader
          sensor={sensor}
          subtitle={sensor.type === 'SER' ? "Smart Edge Relay" : "Smart Edge Outlet"}
          onBack={onBack}
          onClose={onClose}
          onUpdateSensor={onUpdateSensor}
        />

        {/* Mode Selection - Spin Box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-2.5 flex-shrink-0"
        >
          <div className="flex items-center gap-2">
            {/* Spin Box - half width */}
            <div className="flex items-center w-2/3 bg-white/5 rounded-lg border border-white/10 overflow-hidden">
              <button
                onClick={() => navigateMode(-1)}
                className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-r border-white/10 active:scale-90"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="flex-1 text-center py-1.5 relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mode}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className={`text-sm font-bold ${mode === 'always' ? 'text-amber-400' : 'text-cyan-400'}`}
                  >
                    {MODES[currentModeIndex]?.label}
                  </motion.div>
                </AnimatePresence>
              </div>
              <button
                onClick={() => navigateMode(1)}
                className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-l border-white/10 active:scale-90"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Right side: Toggle + Password button */}
            <div className="flex-1 flex items-center justify-end gap-2">
              <AnimatePresence>
                {mode === 'always' && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8, width: 0 }}
                    animate={{ opacity: 1, scale: 1, width: 'auto' }}
                    exit={{ opacity: 0, scale: 0.8, width: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setShowPasswordChange(true)}
                    className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 transition-all active:scale-95 flex-shrink-0"
                    title={t('sensor.set_password')}
                  >
                    <LockIcon className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>

              {mode === 'normal' && (
                <ToggleSwitch isOn={isOn} onToggle={handleToggleOutlet} />
              )}
            </div>
          </div>
        </motion.div>

        <ChartSection
          icon={ZapIcon}
          iconColor="text-violet-400"
          title={t('sensor.energy_usage')}
          data={finalPowerData}
          timeRange={powerTimeRange}
          onTimeRangeChange={setPowerTimeRange}
          disabledRanges={!sensor.active ? ['realtime'] : []}
          chartType="area"
          color="#a78bfa"
          gradientId="seoPower"
          className="flex-1 min-h-0"
          unit={powerTimeRange === 'realtime' ? 'W' : 'kWh'}
          stats={[
            {
              label: powerTimeRange === 'realtime' ? t('sensor.current_power_usage') : 
                     powerTimeRange === 'daily' ? t('sensor.today_total_usage') :
                     powerTimeRange === 'weekly' ? t('sensor.this_week_total_usage') : t('sensor.this_month_total_usage'),
              value: powerTimeRange === 'realtime' 
                ? `${Number(realtimeData?.['power_instant']?.[0]?.value || 0).toFixed(1)} W`
                : `${finalPowerData.length > 0 ? finalPowerData.reduce((sum, item) => sum + (Number(item.value) || 0), 0).toFixed(2) : '0.00'} kWh`,
              color: 'text-violet-400'
            }
          ]}
        />

        {/* Combined Temperature + Humidity Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-3 flex-1 min-h-0 flex flex-col"
        >
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
              <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" />
              <span className="text-[0.9rem] font-bold text-slate-300">
                {t('sensor.indoor_temp_humidity')}
              </span>
            </div>
            <TimeRangeTabs value={envTimeRange} onChange={setEnvTimeRange} disabledRanges={!sensor.active ? ['realtime'] : []} />
          </div>

          <div className="flex items-center gap-3 mb-2 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
              <span className="text-[0.8rem] text-slate-500 uppercase font-bold">{t('sensor.temperature')}</span>
              <span className="text-base font-roboto font-bold text-rose-400">
                {finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1].temp ? finalTempHumidData[finalTempHumidData.length - 1].temp.toFixed(1) : '--'}°C
              </span>
            </div>
            <div className="h-3.5 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span className="text-[0.8rem] text-slate-500 uppercase font-bold">{t('sensor.humidity')}</span>
              <span className="text-base font-roboto font-bold text-cyan-400">
                {finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1].humidity ? finalTempHumidData[finalTempHumidData.length - 1].humidity.toFixed(1) : '--'}%
              </span>
            </div>
          </div>

          <div className="flex-1 min-h-0 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={finalTempHumidData}>
                <defs>
                  <linearGradient id="seoTempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="seoHumidGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" hide />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0a0f1a',
                    borderColor: '#334155',
                    fontSize: '0.7rem',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'temp') return [`${Number(value).toFixed(1)}°C`, t('sensor.temperature')];
                    return [`${Number(value).toFixed(1)}%`, t('sensor.humidity')];
                  }}
                />
                <Area type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#seoTempGrad)" />
                <Area type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#seoHumidGrad)" strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 rounded-full bg-rose-400" />
              <span className="text-slate-500 text-[0.7rem]">{t('sensor.average')}</span>
              <span className="font-roboto text-white">              <span className="text-slate-500 text-[0.7rem]">평균</span>
              <span className="font-mono text-white">
                {finalTempHumidData.length > 0
                  ? `${(finalTempHumidData.reduce((sum: number, item: any) => sum + (item.temp || 0), 0) / finalTempHumidData.length).toFixed(1)}°C`
                  : '--'}
              </span>
</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60" style={{ borderTop: '1px dashed #22d3ee' }} />
              <span className="text-slate-500 text-[0.7rem]">{t('sensor.average')}</span>
              <span className="font-roboto text-white">              <span className="text-slate-500 text-[0.7rem]">평균</span>
              <span className="font-mono text-white">
                {finalTempHumidData.length > 0
                  ? `${(finalTempHumidData.reduce((sum: number, item: any) => sum + (item.humidity || 0), 0) / finalTempHumidData.length).toFixed(1)}%`
                  : '--'}
              </span>
</span>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showPasswordVerify && (
          <PasswordToast
            type="verify"
            storedPassword={password}
            onClose={() => {
              setShowPasswordVerify(false);
              setPendingModeIndex(null);
            }}
            onVerify={handlePasswordVerified}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPasswordChange && (
          <PasswordToast
            type="change"
            storedPassword={password}
            onClose={() => setShowPasswordChange(false)}
            onChangeComplete={handlePasswordChanged}
          />
        )}
      </AnimatePresence>
    </>
  );
}
